if (!customElements.get('pdp-personalized')) {
  class PdpPersonalized extends HTMLElement {
    connectedCallback() {
      if (/iPhone|iPad|iPod/.test(navigator.userAgent)) {
        document.documentElement.classList.add('is-ios');
      }
      this.autoplayVideo = this.dataset.autoplay !== 'false';
      this.mainImg = this.querySelector('.pdp-pers__main-img');
      this.mainImg.style.display = 'block';
      this.mainVideo = null;
      this.playBtn = this.querySelector('[data-play-btn]');
      if (this.playBtn) this.playBtn.style.display = 'none';
      this.thumbStrip = this.querySelector('[data-thumb-strip]');
      this.priceEl = this.querySelector('.pdp-pers__price');
      this.materialOpts = this.querySelectorAll('.pdp-pers__material-opt');

      const mapEl = this.querySelector('[data-variant-gallery-map]');
      const mapData = mapEl ? JSON.parse(mapEl.textContent) : { optionGalleries: {}, variantOptionMap: {}, fallback: [] };
      this.optionGalleries = mapData.optionGalleries;
      this.variantOptionMap = mapData.variantOptionMap;
      this.fallbackImages = mapData.fallback || [];

      // Cache thumb classes from server-rendered markup to avoid Tailwind JIT misses
      const firstThumb = this.thumbStrip.querySelector('.pdp-pers__thumb');
      this.thumbBtnClass = firstThumb ? firstThumb.className : '';
      this.thumbImgClass = firstThumb ? firstThumb.querySelector('img')?.className : '';

      this.mq = window.matchMedia('(min-width: 990px)');
      this._mqHandler = () => this.buildGallery(this.currentVariantId);
      this.mq.addEventListener('change', this._mqHandler);

      const activeOpt = this.querySelector('.pdp-pers__material-opt[aria-current="true"]');
      this.currentVariantId = activeOpt ? activeOpt.dataset.variantId : null;

      if (this.playBtn) {
        this.playBtn.addEventListener('click', () => this._togglePlayPause());
      }

      this._bindThumbs();
      this.materialOpts.forEach(opt => {
        opt.addEventListener('click', () => this.selectVariant(opt));
      });

      this.buildGallery(this.currentVariantId);
    }

    disconnectedCallback() {
      this.mq.removeEventListener('change', this._mqHandler);
    }

    _bindThumbs() {
      this.thumbStrip.querySelectorAll('.pdp-pers__thumb').forEach(thumb => {
        thumb.addEventListener('click', () => this.setThumb(thumb));
      });
    }

    _togglePlayPause() {
      if (!this.mainVideo) return;
      if (this.mainVideo.paused) {
        this.mainVideo.play().catch(() => {});
        this._setPlayBtnState('playing');
      } else {
        this.mainVideo.pause();
        this._setPlayBtnState('paused');
      }
    }

    _setPlayBtnState(state) {
      if (!this.playBtn) return;
      const playIcon = this.playBtn.querySelector('[data-play-icon]');
      const pauseIcon = this.playBtn.querySelector('[data-pause-icon]');
      if (state === 'playing') {
        playIcon.style.display = 'none';
        pauseIcon.style.display = '';
        this.playBtn.setAttribute('aria-label', 'Pause video');
      } else {
        playIcon.style.display = '';
        pauseIcon.style.display = 'none';
        this.playBtn.setAttribute('aria-label', 'Play video');
      }
    }

    _setMainMedia(item) {
      if (item.type === 'video') {
        if (!this.mainVideo) {
          this.mainVideo = document.createElement('video');
          this.mainVideo.className = this.mainImg.className;
          this.mainVideo.autoplay = this.autoplayVideo;
          this.mainVideo.muted = true;
          this.mainVideo.loop = true;
          this.mainVideo.setAttribute('playsinline', '');
          this.mainVideo.style.display = 'none';
          this.mainImg.parentElement.insertBefore(this.mainVideo, this.mainImg);
        }
        this.mainVideo.src = item.videoSrc;
        if (item.poster) this.mainVideo.poster = item.poster;
        this.mainVideo.load();
        if (this.autoplayVideo) this.mainVideo.play().catch(() => {});
        this.mainVideo.style.display = 'block';
        this.mainImg.style.display = 'none';
        if (this.playBtn) {
          this.playBtn.style.display = 'flex';
          this._setPlayBtnState(this.autoplayVideo ? 'playing' : 'paused');
        }
      } else {
        this.mainImg.srcset = '';
        this.mainImg.src = item.src;
        this.mainImg.alt = item.alt || '';
        this.mainImg.style.display = 'block';
        if (this.mainVideo) {
          this.mainVideo.pause();
          this.mainVideo.style.display = 'none';
        }
        if (this.playBtn) this.playBtn.style.display = 'none';
      }
    }

    getImages(variantId) {
      const optionHandle = this.variantOptionMap[variantId];
      const optionData = optionHandle && this.optionGalleries[optionHandle];
      if (optionData) {
        const key = this.mq.matches ? 'desktop' : 'mobile';
        const images = optionData[key];
        if (images && images.length) return images;
      }
      return this.fallbackImages;
    }

    buildGallery(variantId) {
      const images = this.getImages(variantId);
      if (!images.length) return;

      this._setMainMedia(images[0]);

      this.thumbStrip.innerHTML = images.map((item, i) => {
        const alt = (item.alt || '').replace(/"/g, '&quot;');
        const thumbSrc = item.thumb || item.poster || '';
        return `<button class="${this.thumbBtnClass}" data-src="${item.src || ''}" data-video-src="${item.videoSrc || ''}" data-poster="${item.poster || ''}" data-type="${item.type || 'image'}" data-alt="${alt}" aria-current="${i === 0 ? 'true' : 'false'}">
          <img src="${thumbSrc}" alt="${alt}" loading="lazy" class="${this.thumbImgClass}" />
        </button>`;
      }).join('');

      this._bindThumbs();
    }

    setThumb(thumb) {
      const type = thumb.dataset.type || 'image';
      this._setMainMedia({
        type,
        src: thumb.dataset.src,
        videoSrc: thumb.dataset.videoSrc,
        poster: thumb.dataset.poster,
        alt: thumb.dataset.alt
      });
      this.thumbStrip.querySelectorAll('.pdp-pers__thumb').forEach(t => t.setAttribute('aria-current', 'false'));
      thumb.setAttribute('aria-current', 'true');
    }

    selectVariant(opt) {
      this.materialOpts.forEach(o => o.setAttribute('aria-current', 'false'));
      opt.setAttribute('aria-current', 'true');
      if (this.priceEl && opt.dataset.price) this.priceEl.textContent = opt.dataset.price;
      history.replaceState({}, '', '?variant=' + opt.dataset.variantId);
      this.currentVariantId = opt.dataset.variantId;
      this.buildGallery(this.currentVariantId);
    }
  }
  customElements.define('pdp-personalized', PdpPersonalized);
}
